import React from "react";
import { MARKETS } from "../data/site";

// Simplified world map with country dots on a dark canvas.
// Uses a lightweight embedded SVG of continents (equirectangular projection).

const CONTINENTS_PATH = `M158,142 L172,140 L186,146 L200,142 L220,148 L232,144 L244,148 L262,152 L282,158 L304,164 L322,172 L338,176 L352,168 L364,172 L376,180 L390,182 L404,176 L418,180 L432,188 L448,192 L462,188 L476,192 L488,196 L500,192 L512,188 L522,196 L534,204 L546,208 L554,220 L562,228 L570,240 L582,250 L594,258 L606,262 L618,268 L630,274 L640,282 L650,290 L656,300 L648,308 L640,314 L628,318 L616,314 L602,312 L588,310 L574,308 L560,314 L546,320 L534,326 L522,332 L510,336 L498,340 L484,342 L470,340 L458,338 L446,332 L436,324 L424,318 L412,320 L400,326 L386,332 L370,336 L354,340 L338,338 L322,336 L308,332 L294,326 L280,318 L266,310 L252,300 L238,290 L226,278 L214,266 L204,254 L196,242 L188,228 L180,214 L172,198 L166,182 L162,168 L158,154 Z`;

export default function GlobalMarkets() {
  return (
    <section id="markets" data-testid="markets-section" className="py-20 lg:py-32 bg-[#F0F4F8] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <div className="overline mb-4">Global Markets</div>
          <h2 className="section-heading text-navy text-3xl md:text-4xl lg:text-5xl font-bold">
            Serving Importers Across Global Markets
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Reliable Indian FMCG sourcing delivered across 18+ countries and growing.
          </p>
        </div>

        {/* Map container with navy background + dotted world */}
        <div className="relative rounded-3xl overflow-hidden bg-navy border border-navy shadow-2xl">
          <div className="absolute inset-0 grid-overlay opacity-10" />
          <div className="relative aspect-[2/1] w-full">
            {/* Dotted world map background */}
            <DottedWorld />

            {/* Country pins */}
            {MARKETS.map((m) => (
              <div
                key={m.name}
                data-testid={`market-pin-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="absolute group"
                style={{ left: `${m.x}%`, top: `${m.y}%` }}
                title={m.name}
              >
                <div className="relative -translate-x-1/2 -translate-y-1/2">
                  <div className="w-3 h-3 rounded-full bg-gold pulse-dot ring-4 ring-gold/25" />
                  <span className="ping-ring bg-gold/40" />
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <span className="px-2.5 py-1 rounded-md bg-white text-navy text-[11px] font-bold shadow-lg">
                      {m.name}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {MARKETS.map((m) => (
            <div
              key={m.name}
              data-testid={`market-tag-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-4 py-3 rounded-xl bg-white border border-slate-100 text-sm font-medium text-navy text-center card-hover"
            >
              {m.name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Dotted world silhouette — created via CSS radial-gradient dots pattern that roughly outlines continents.
function DottedWorld() {
  return (
    <svg
      viewBox="0 0 200 100"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full opacity-60"
      aria-hidden="true"
    >
      <defs>
        <pattern id="dotpat" width="2" height="2" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.55" fill="rgba(255,255,255,0.55)" />
        </pattern>
        <mask id="continents">
          {/* Rough continent silhouettes (equirectangular). White = show dots. */}
          <rect width="200" height="100" fill="black" />
          {/* North America */}
          <path d="M18,20 L36,18 L48,22 L58,28 L62,36 L58,44 L52,50 L46,54 L38,56 L30,54 L22,50 L18,44 L14,36 L14,28 Z" fill="white"/>
          {/* Central America / Caribbean */}
          <path d="M40,52 L52,54 L54,60 L48,64 L42,62 Z" fill="white"/>
          {/* South America */}
          <path d="M52,60 L60,60 L64,66 L64,74 L60,82 L54,88 L48,88 L44,82 L44,72 L48,66 Z" fill="white"/>
          {/* Europe */}
          <path d="M96,26 L108,24 L114,30 L116,36 L112,40 L104,42 L96,40 L92,34 Z" fill="white"/>
          {/* Africa */}
          <path d="M96,44 L108,44 L114,50 L118,58 L116,68 L112,76 L106,82 L100,84 L94,80 L90,72 L88,62 L90,52 Z" fill="white"/>
          {/* Middle East / South Asia */}
          <path d="M116,40 L128,40 L134,44 L134,52 L128,56 L120,54 L116,50 Z" fill="white"/>
          {/* Asia mainland */}
          <path d="M118,26 L140,22 L160,24 L170,30 L172,38 L166,44 L156,46 L142,44 L128,42 L120,36 Z" fill="white"/>
          {/* SE Asia */}
          <path d="M148,48 L158,48 L162,54 L160,60 L152,62 L146,58 Z" fill="white"/>
          {/* Australia */}
          <path d="M158,72 L174,70 L182,74 L184,80 L178,84 L166,86 L158,82 Z" fill="white"/>
          {/* New Zealand */}
          <path d="M188,84 L194,84 L194,90 L188,90 Z" fill="white"/>
          {/* Madagascar */}
          <path d="M118,74 L122,74 L124,80 L120,82 Z" fill="white"/>
          {/* Japan */}
          <path d="M170,34 L176,34 L176,40 L170,40 Z" fill="white"/>
          {/* UK */}
          <path d="M92,26 L96,26 L96,30 L92,30 Z" fill="white"/>
        </mask>
      </defs>
      <rect width="200" height="100" fill="url(#dotpat)" mask="url(#continents)" />
    </svg>
  );
}
